import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
} from '@mui/material';
import { useTheme } from '../../../ThemeContext';

// Icons
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import VideoSettingsRoundedIcon from '@mui/icons-material/VideoSettingsRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';

// Images
import SS1 from '../../../assets/SS1.png';
import sb1 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_52_21 PM.png';
import sb2 from '../../../assets/ChatGPT Image Aug 24, 2026, 01_54_39 PM.png';
import editorShot1 from '../../../assets/editor/editor_shot_1.png';
import editorIcon from '../../../assets/editor/editor_icon.png';

const pipelineStages = [
  {
    id: 0,
    step: '01',
    product: 'Filmhook AI',
    tag: 'Pre-Production',
    phaseName: 'Script & Visual Conceptualization',
    headline: 'From Raw Idea to Production-Ready Screenplay & Storyboards',
    desc: 'Begin your production inside Filmhook AI. The neural engine transforms your loglines into standard screenplay beat sheets, writes multi-character dialogue, and renders camera-ready storyboard shots with cinematic lighting specifications.',
    color: '#06b6d4',
    bg: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.35)',
    icon: <AutoAwesomeRoundedIcon sx={{ fontSize: 28 }} />,
    deliverables: [
      'Final Draft (.fdx) Screenplay',
      'Scene Breakdown & Beat Sheets',
      '2.39:1 Anamorphic Concept Storyboard',
      'Character Bios & Casting Radar Specs',
    ],
    flowFrom: 'Director Concept & Beat Prompts',
    flowTo: 'Filmhook Apps Casting Engine',
    previewType: 'ai-storyboard',
  },
  {
    id: 1,
    step: '02',
    product: 'Filmhook Apps',
    tag: 'Casting & Crew Logistics',
    phaseName: 'Talent Discovery & Location Management',
    headline: 'Cast Verified Actors & Mobilize Your Complete Film Crew',
    desc: 'The character specs created in Filmhook AI directly populate casting calls in Filmhook Apps. Verified actors, cinematographers, and technicians apply with showreels. Conduct digital auditions, book shooting locations, and assemble your call sheet.',
    color: '#07a4f5',
    bg: 'rgba(7, 164, 245, 0.12)',
    border: 'rgba(7, 164, 245, 0.35)',
    icon: <PhoneIphoneRoundedIcon sx={{ fontSize: 28 }} />,
    deliverables: [
      'Verified Actor Video Auditions',
      'Bio Match Compatibility (up to 98%)',
      'Digital Production Call Sheets',
      'Location Bookings & Crew Rosters',
    ],
    flowFrom: 'Filmhook AI Character Specs',
    flowTo: 'Filmhook Editor Cloud Dailies',
    previewType: 'apps-casting',
  },
  {
    id: 2,
    step: '03',
    product: 'Filmhook Editor',
    tag: 'Post-Production',
    phaseName: 'Cloud Timeline Editing & Color Grading',
    headline: 'Edit 4K Footage, Apply Cinema LUTs & Mix Audio Together',
    desc: 'Approved audition tapes, scene recordings, and camera cards sync straight into the Filmhook Editor cloud bins. Directors and editors collaborate on multi-track video and audio timelines, apply custom 3D LUTs, and lock picture without slow file downloads.',
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.12)',
    border: 'rgba(139, 92, 246, 0.35)',
    icon: <VideoSettingsRoundedIcon sx={{ fontSize: 28 }} />,
    deliverables: [
      'Multi-Track 4K ProRes NLE Timeline',
      'Cinema 3D LUT Color Grading Nodes',
      'Synchronized Dialogue & Foley Mix',
      'Real-Time Director Review Comments',
    ],
    flowFrom: 'On-Set Dailies & Audition Takes',
    flowTo: 'Filmhook Apps Premiere & Distribution',
    previewType: 'editor-nle',
  },
  {
    id: 3,
    step: '04',
    product: 'Filmhook Apps',
    tag: 'Distribution & Audience',
    phaseName: 'Community Premiere & Global Reach',
    headline: 'Release Your Film, Engage Fans & Connect with Distributors',
    desc: 'Export the final master directly from Filmhook Editor into Filmhook Apps for audience release. Screen trailers to a targeted film community, host live interactive filmmaker Q&As, gather verified audience reviews, and submit to film festivals.',
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.12)',
    border: 'rgba(16, 185, 129, 0.35)',
    icon: <MovieFilterRoundedIcon sx={{ fontSize: 28 }} />,
    deliverables: [
      'Direct-to-Community Trailer Premiere',
      'Audience Ratings & Fan Reviews',
      'Live Streaming Director Q&A',
      'Festival Submission Screeners',
    ],
    flowFrom: 'Filmhook Editor Final Master Cut',
    flowTo: 'Global Cinema Audiences & Critics',
    previewType: 'premiere-stream',
  },
];

const ecosystemBridges = [
  {
    icon: <SyncAltRoundedIcon sx={{ color: '#06b6d4', fontSize: 24 }} />,
    title: 'AI ➔ Apps Direct Bridge',
    subtitle: 'Screenplay to Casting Call',
    desc: 'Character profiles generated by Filmhook AI automatically create structured audition posts in Filmhook Apps with one tap.',
    color: '#06b6d4',
  },
  {
    icon: <HubRoundedIcon sx={{ color: '#8b5cf6', fontSize: 24 }} />,
    title: 'Apps ➔ Editor Direct Bridge',
    subtitle: 'Audition Takes to Cloud Timeline',
    desc: 'Audition clips and on-set shoot media uploaded by crew automatically organize into the project bins of Filmhook Editor.',
    color: '#8b5cf6',
  },
  {
    icon: <CloudDoneRoundedIcon sx={{ color: '#10b981', fontSize: 24 }} />,
    title: 'Editor ➔ Release Direct Bridge',
    subtitle: 'Timeline Master to Global Premiere',
    desc: 'Export final cuts without format conversion directly to Filmhook Apps for film festival screeners and audience distribution.',
    color: '#10b981',
  },
];

const ProductEcosystemSection = () => {
  const { isDarkMode } = useTheme();
  const [activeStage, setActiveStage] = useState(0);

  const stage = pipelineStages[activeStage];

  const handleNext = () => {
    setActiveStage((prev) => (prev + 1) % pipelineStages.length);
  };

  const handlePrev = () => {
    setActiveStage((prev) => (prev - 1 + pipelineStages.length) % pipelineStages.length);
  };

  return (
    <Box
      id="how-products-work-together"
      sx={{
        py: { xs: 8, md: 13 },
        backgroundColor: isDarkMode ? '#030712' : '#f8fafc',
        borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
        borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decorative Ambient Lights */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '5%',
          width: 360,
          height: 360,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${stage.color}22 0%, transparent 70%)`,
          filter: 'blur(80px)',
          pointerEvents: 'none',
          transition: 'background 0.5s ease',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '8%',
          width: 380,
          height: 380,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
          filter: 'blur(90px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 5, md: 7 }}>
          <Chip
            icon={<HubRoundedIcon sx={{ fontSize: '16px !important', color: `${stage.color} !important` }} />}
            label="UNIFIED CINEMA PIPELINE"
            sx={{
              fontWeight: 800,
              fontSize: '0.74rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: stage.color,
              backgroundColor: isDarkMode ? `${stage.color}18` : `${stage.color}12`,
              border: `1px solid ${stage.color}40`,
              mb: 2,
              px: 1,
              transition: 'all 0.3s ease',
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' },
              fontWeight: 900,
              color: isDarkMode ? '#ffffff' : '#0d1530',
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            How The 3 Products Work Together
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: isDarkMode ? '#94a3b8' : '#64748b',
              maxWidth: 720,
              mx: 'auto',
              fontSize: { xs: '0.98rem', md: '1.1rem' },
              lineHeight: 1.7,
            }}
          >
            Filmhook eliminates fragmented filmmaking tools. Screenplay ideas generated in <strong>Filmhook AI</strong> immediately sync into casting calls in <strong>Filmhook Apps</strong>, while audition dailies seamlessly import onto <strong>Filmhook Editor’s</strong> timeline for final master delivery.
          </Typography>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            INTERACTIVE PIPELINE STEPPER / FLOW TRACK
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 5,
          }}
        >
          {pipelineStages.map((st, index) => {
            const isActive = index === activeStage;
            return (
              <Box
                key={st.id}
                onClick={() => setActiveStage(index)}
                sx={{
                  p: 2.2,
                  borderRadius: '18px',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  background: isActive
                    ? isDarkMode
                      ? `linear-gradient(135deg, ${st.color}25 0%, #0d1527 100%)`
                      : `linear-gradient(135deg, ${st.color}15 0%, #ffffff 100%)`
                    : isDarkMode
                    ? '#0d1527'
                    : '#ffffff',
                  border: isActive
                    ? `2px solid ${st.color}`
                    : isDarkMode
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid #e2e8f0',
                  boxShadow: isActive
                    ? `0 12px 30px ${st.color}35`
                    : isDarkMode
                    ? '0 6px 18px rgba(0,0,0,0.3)'
                    : '0 6px 18px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isActive ? 'translateY(-4px)' : 'none',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: st.color,
                  },
                }}
              >
                {/* Active Top Glow Line */}
                {isActive && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: st.color,
                    }}
                  />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 900,
                      color: isActive ? st.color : isDarkMode ? '#64748b' : '#94a3b8',
                    }}
                  >
                    PHASE {st.step}
                  </Typography>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      bgcolor: isActive ? st.color : isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                      color: isActive ? '#fff' : isDarkMode ? '#94a3b8' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {st.icon}
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    color: st.color,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: '0.68rem',
                    letterSpacing: '0.04em',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  {st.product}
                </Typography>

                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{
                    color: isDarkMode ? '#ffffff' : '#0f172a',
                    fontSize: '0.92rem',
                    lineHeight: 1.3,
                  }}
                >
                  {st.tag}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ══════════════════════════════════════════════════════════
            HERO ACTIVE STAGE DEEP DIVE SHOWCASE CARD
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            borderRadius: '28px',
            p: { xs: 3, md: 5 },
            background: isDarkMode
              ? 'linear-gradient(135deg, #091224 0%, #050b17 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: isDarkMode
              ? `1.5px solid ${stage.border}`
              : `1.5px solid ${stage.color}35`,
            boxShadow: isDarkMode
              ? `0 24px 70px rgba(0,0,0,0.8), 0 0 50px ${stage.color}15`
              : `0 24px 70px rgba(0,0,0,0.08), 0 0 40px ${stage.color}15`,
            position: 'relative',
            overflow: 'hidden',
            mb: 6,
          }}
        >
          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
            {/* Left Column: Stage Breakdown & Data Transfer Details */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Chip
                  label={`PHASE ${stage.step} // ${stage.product}`}
                  sx={{
                    fontWeight: 900,
                    fontSize: '0.74rem',
                    letterSpacing: '0.06em',
                    color: stage.color,
                    bgcolor: stage.bg,
                    border: `1px solid ${stage.color}50`,
                  }}
                />
                <Chip
                  label={stage.tag}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                  }}
                />
              </Box>

              <Typography
                variant="h4"
                fontWeight={900}
                sx={{
                  color: isDarkMode ? '#ffffff' : '#0d1530',
                  fontSize: { xs: '1.5rem', sm: '1.9rem', md: '2.2rem' },
                  lineHeight: 1.25,
                  mb: 2,
                }}
              >
                {stage.headline}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontSize: '0.96rem',
                  lineHeight: 1.75,
                  mb: 3.5,
                }}
              >
                {stage.desc}
              </Typography>

              {/* Data Flow Bridge Box */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: '16px',
                  background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f1f5f9',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                  mb: 3.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: stage.color,
                    fontWeight: 800,
                    fontSize: '0.68rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1.2,
                  }}
                >
                  ⚡ AUTOMATED DATA PIPELINE SYNC
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: '0.68rem', fontWeight: 700 }}>
                      INCOMING INPUT
                    </Typography>
                    <Typography sx={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', fontSize: '0.82rem', fontWeight: 700 }}>
                      {stage.flowFrom}
                    </Typography>
                  </Box>
                  <ArrowForwardRoundedIcon
                    sx={{
                      color: stage.color,
                      fontSize: 20,
                      transform: { xs: 'rotate(90deg)', sm: 'none' },
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: '0.68rem', fontWeight: 700 }}>
                      SEAMLESS OUTPUT
                    </Typography>
                    <Typography sx={{ color: stage.color, fontSize: '0.82rem', fontWeight: 800 }}>
                      {stage.flowTo}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Deliverables checklist */}
              <Box sx={{ mb: 3.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    fontWeight: 700,
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  PIPELINE DELIVERABLES:
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
                  {stage.deliverables.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleRoundedIcon sx={{ color: stage.color, fontSize: 18 }} />
                      <Typography
                        sx={{
                          color: isDarkMode ? '#cbd5e1' : '#334155',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                        }}
                      >
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Step Navigation Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  onClick={handlePrev}
                  startIcon={<ArrowBackRoundedIcon />}
                  sx={{
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    borderRadius: '20px',
                    px: 2,
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #cbd5e1',
                    '&:hover': {
                      color: stage.color,
                      borderColor: stage.color,
                    },
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNext}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    background: `linear-gradient(90deg, ${stage.color} 0%, #3b82f6 100%)`,
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    borderRadius: '20px',
                    px: 3,
                    py: 1,
                    boxShadow: `0 6px 20px ${stage.color}40`,
                    '&:hover': {
                      opacity: 0.95,
                      boxShadow: `0 8px 24px ${stage.color}55`,
                    },
                  }}
                >
                  Next: Phase {pipelineStages[(activeStage + 1) % pipelineStages.length].step}
                </Button>
                <Typography sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', fontSize: '0.8rem', fontWeight: 700, ml: 'auto' }}>
                  Step {activeStage + 1} of 4
                </Typography>
              </Box>
            </Grid>

            {/* Right Column: Live Visual Simulator of that Stage */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: '24px',
                  p: { xs: 2.5, sm: 3 },
                  background: isDarkMode ? '#050b17' : '#0d1527',
                  border: `1.5px solid ${stage.color}40`,
                  boxShadow: `0 20px 60px rgba(0,0,0,0.8), 0 0 40px ${stage.color}20`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Top Simulation Bar */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 2,
                    mb: 2.5,
                    borderBottom: '1px solid #1e293b',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                    <Typography sx={{ color: '#cbd5e1', fontSize: '0.78rem', fontWeight: 800, ml: 1 }}>
                      {stage.product} • Live Pipeline Feed
                    </Typography>
                  </Box>
                  <Chip
                    label={`Phase ${stage.step} Synced`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: `${stage.color}25`,
                      color: stage.color,
                      border: `1px solid ${stage.color}50`,
                    }}
                  />
                </Box>

                {/* 1. Preview for Stage 0: AI Script & Storyboard */}
                {activeStage === 0 && (
                  <Box>
                    <Box sx={{ p: 1.8, borderRadius: '12px', bgcolor: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.25)', mb: 2 }}>
                      <Typography sx={{ color: '#06b6d4', fontSize: '0.68rem', fontWeight: 800, mb: 0.5 }}>
                        PROMPT GENERATION // BANGALORE CANAL NOIR
                      </Typography>
                      <Typography sx={{ color: '#f1f5f9', fontSize: '0.82rem', fontStyle: 'italic', lineHeight: 1.4 }}>
                        "ARJUN (38): 'Death is a matter of paperwork here. The syndicate didn't kill me. They hired me.'"
                      </Typography>
                    </Box>

                    {/* Stills Showcase */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                      <Box sx={{ height: 130, borderRadius: '12px', overflow: 'hidden', position: 'relative', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                        <Box component="img" src={sb1} alt="Shot 01" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <Box sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: 'rgba(0,0,0,0.8)', px: 0.8, py: 0.2, borderRadius: '4px' }}>
                          <Typography sx={{ color: '#22d3ee', fontSize: '0.62rem', fontWeight: 800 }}>Shot 01 • Wide Master</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ height: 130, borderRadius: '12px', overflow: 'hidden', position: 'relative', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                        <Box component="img" src={sb2} alt="Shot 02" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <Box sx={{ position: 'absolute', bottom: 6, left: 6, bgcolor: 'rgba(0,0,0,0.8)', px: 0.8, py: 0.2, borderRadius: '4px' }}>
                          <Typography sx={{ color: '#38bdf8', fontSize: '0.62rem', fontWeight: 800 }}>Shot 02 • High Tension</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#0b162c', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeRoundedIcon sx={{ color: '#06b6d4', fontSize: 18 }} />
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 700 }}>
                          Auto-Transferred to Casting Call: <strong style={{ color: '#fff' }}>Antagonist Detective</strong>
                        </Typography>
                      </Box>
                      <Chip label="Ready for Apps →" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#06b6d4', color: '#fff' }} />
                    </Box>
                  </Box>
                )}

                {/* 2. Preview for Stage 1: Filmhook Apps Casting */}
                {activeStage === 1 && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 150,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '1.5px solid #07a4f5',
                          flexShrink: 0,
                          boxShadow: '0 8px 24px rgba(7, 164, 245, 0.3)',
                        }}
                      >
                        <Box component="img" src={SS1} alt="Filmhook App" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(7, 164, 245, 0.1)', border: '1px solid rgba(7, 164, 245, 0.3)', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 800 }}>
                              Applicant: Madhu S
                            </Typography>
                            <Chip label="98% Match" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#10b981', color: '#fff' }} />
                          </Box>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.74rem' }}>
                            Role: Lead Detective • Language: Kannada / Malayalam
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1.2, borderRadius: '10px', bgcolor: '#0a1428', border: '1px solid #1e293b' }}>
                          <Typography sx={{ color: '#07a4f5', fontSize: '0.72rem', fontWeight: 800 }}>
                            ✓ 4K Showreel & Video Audition Verified
                          </Typography>
                          <Typography sx={{ color: '#cbd5e1', fontSize: '0.7rem', mt: 0.3 }}>
                            Shooting Location: Bangalore Palace Grounds Booked
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#0b162c', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 700 }}>
                        Takes Synced to Filmhook Editor Bins
                      </Typography>
                      <Chip label="Dailies Ready →" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#07a4f5', color: '#fff' }} />
                    </Box>
                  </Box>
                )}

                {/* 3. Preview for Stage 2: Filmhook Editor NLE */}
                {activeStage === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 85,
                          height: 155,
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '1.5px solid #8b5cf6',
                          flexShrink: 0,
                          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.35)',
                          position: 'relative',
                          bgcolor: '#0f172a',
                        }}
                      >
                        <Box
                          component="img"
                          src={editorShot1}
                          alt="Filmhook Editor Play Store Preview"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Box
                                component="img"
                                src={editorIcon}
                                alt="Editor Icon"
                                sx={{ width: 22, height: 22, borderRadius: '6px' }}
                              />
                              <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 800 }}>
                                Filmhook Editor
                              </Typography>
                            </Box>
                            <Chip label="Play Store Verified" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#8b5cf6', color: '#fff' }} />
                          </Box>
                          <Typography sx={{ color: '#cbd5e1', fontSize: '0.74rem' }}>
                            Multi-track NLE • AI Cutout • 4K Face Enhancer
                          </Typography>
                        </Box>

                        <Box sx={{ p: 1.2, borderRadius: '10px', bgcolor: '#0a1428', border: '1px solid #1e293b' }}>
                          <Typography sx={{ color: '#a78bfa', fontSize: '0.72rem', fontWeight: 800 }}>
                            ✓ 1-Tap Cutout & High-FPS Timeline Rendered
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', mt: 0.3 }}>
                            Synchronized audio stems & dialogue master locked
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#0b162c', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 700 }}>
                        Graded Master Picture Locked
                      </Typography>
                      <Chip label="Export to Premiere →" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#8b5cf6', color: '#fff' }} />
                    </Box>
                  </Box>
                )}

                {/* 4. Preview for Stage 3: Premiere & Distribution */}
                {activeStage === 3 && (
                  <Box>
                    <Box sx={{ p: 2, borderRadius: '14px', bgcolor: 'rgba(16, 185, 129, 0.1)', border: '1.5px solid rgba(16, 185, 129, 0.35)', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem' }}>
                          "Canal Syndicate" • Official Premiere
                        </Typography>
                        <Chip label="Live on Filmhook" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#10b981', color: '#fff' }} />
                      </Box>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', mb: 1.5 }}>
                        Streamed in 4K UHD with 5.1 Dolby Surround Sound to global cinephiles.
                      </Typography>

                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                        <Box sx={{ p: 1, borderRadius: '8px', bgcolor: '#050b17', textAlign: 'center' }}>
                          <Typography sx={{ color: '#10b981', fontWeight: 800, fontSize: '0.9rem' }}>48.5K</Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.62rem' }}>Views</Typography>
                        </Box>
                        <Box sx={{ p: 1, borderRadius: '8px', bgcolor: '#050b17', textAlign: 'center' }}>
                          <Typography sx={{ color: '#fbbf24', fontWeight: 800, fontSize: '0.9rem' }}>4.9★</Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.62rem' }}>Audience</Typography>
                        </Box>
                        <Box sx={{ p: 1, borderRadius: '8px', bgcolor: '#050b17', textAlign: 'center' }}>
                          <Typography sx={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.9rem' }}>14</Typography>
                          <Typography sx={{ color: '#64748b', fontSize: '0.62rem' }}>Festival Invites</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#0b162c', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 700 }}>
                        Direct Audience Ratings & Distribution Deals
                      </Typography>
                      <Chip label="Full Pipeline Complete ✓" size="small" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#10b981', color: '#fff' }} />
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            3 INTERCONNECTED ECOSYSTEM DATA BRIDGES
            ══════════════════════════════════════════════════════════ */}
        <Box sx={{ mb: 6 }}>
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h5"
              fontWeight={900}
              sx={{ color: isDarkMode ? '#ffffff' : '#0d1530', mb: 1 }}
            >
              Zero-Friction Bridges Connecting The Suite
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', maxWidth: 560, mx: 'auto' }}
            >
              How data passes instantly from pre-production to filming and post without exporting or re-uploading files.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {ecosystemBridges.map((bridge, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: '20px',
                    height: '100%',
                    background: isDarkMode ? '#0d1527' : '#ffffff',
                    border: isDarkMode
                      ? '1px solid rgba(255,255,255,0.08)'
                      : '1px solid #e2e8f0',
                    boxShadow: isDarkMode
                      ? '0 10px 30px rgba(0,0,0,0.4)'
                      : '0 10px 30px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: bridge.color,
                      boxShadow: `0 16px 36px ${bridge.color}25`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      bgcolor: `${bridge.color}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    {bridge.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: bridge.color,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      letterSpacing: '0.04em',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    {bridge.subtitle}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{
                      color: isDarkMode ? '#fff' : '#0f172a',
                      fontSize: '1.05rem',
                      mb: 1,
                    }}
                  >
                    {bridge.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      fontSize: '0.86rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {bridge.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ══════════════════════════════════════════════════════════
            ECOSYSTEM VALUE PILLARS (Bottom Banner)
            ══════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            p: { xs: 3, md: 3.5 },
            borderRadius: '22px',
            background: isDarkMode
              ? 'linear-gradient(90deg, rgba(7, 164, 245, 0.08) 0%, rgba(139, 92, 246, 0.08) 50%, rgba(6, 182, 212, 0.08) 100%)'
              : 'linear-gradient(90deg, #f0f9ff 0%, #faf5ff 50%, #ecfeff 100%)',
            border: isDarkMode
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid #e0e7ff',
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2.5,
            textAlign: 'center',
          }}
        >
          {[
            { label: 'Single Unified Account', desc: '1 login across AI, Apps & Editor', icon: <SecurityRoundedIcon sx={{ color: '#07a4f5' }} /> },
            { label: 'Zero File Re-Uploads', desc: 'Continuous cloud asset pipeline', icon: <CloudDoneRoundedIcon sx={{ color: '#8b5cf6' }} /> },
            { label: 'Real-Time Sync', desc: 'Director, cast & crew aligned', icon: <SyncAltRoundedIcon sx={{ color: '#06b6d4' }} /> },
            { label: 'Multi-Language Cinema', desc: 'Kannada, Malayalam, Hindi & more', icon: <LanguageRoundedIcon sx={{ color: '#10b981' }} /> },
          ].map((item, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ mb: 1 }}>{item.icon}</Box>
              <Typography fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '0.88rem' }}>
                {item.label}
              </Typography>
              <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mt: 0.3 }}>
                {item.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ProductEcosystemSection;
